import type { EpochMs, LucideIconName, MockCache, User, UserGroup, UserGroupId, UserId, UserRole } from './types';

export const ADMIN_GROUP_ID = 'ugr_role_admin' as UserGroupId;
export const MANAGER_GROUP_ID = 'ugr_role_manager' as UserGroupId;
export const MEMBER_GROUP_ID = 'ugr_role_member' as UserGroupId;

const SYSTEM_GROUP_ROLES: ReadonlyMap<UserGroupId, UserRole> = new Map<UserGroupId, UserRole>([
  [ADMIN_GROUP_ID, 'admin'],
  [MANAGER_GROUP_ID, 'manager'],
  [MEMBER_GROUP_ID, 'member'],
]);

const ROLE_GROUP_IDS: Readonly<Record<UserRole, UserGroupId>> = {
  admin: ADMIN_GROUP_ID,
  manager: MANAGER_GROUP_ID,
  member: MEMBER_GROUP_ID,
};

export function systemGroupRole(id: UserGroupId): UserRole | null {
  return SYSTEM_GROUP_ROLES.get(id) ?? null;
}

function systemGroup(cache: MockCache, id: UserGroupId, name: string, icon: string): UserGroup {
  return {
    id,
    workspaceId: cache.activeWorkspaceId,
    name,
    icon: icon as LucideIconName,
    color: null,
    createdBy: cache.me,
    createdAt: 0 as EpochMs,
    sortKey: '',
    archivedAt: null,
  };
}

function systemGroups(cache: MockCache): UserGroup[] {
  return [
    systemGroup(cache, ADMIN_GROUP_ID, 'Администраторы', 'ShieldCheck'),
    systemGroup(cache, MANAGER_GROUP_ID, 'Менеджеры', 'UserCog'),
    systemGroup(cache, MEMBER_GROUP_ID, 'Участники', 'Users'),
  ];
}

function bySortKey(left: UserGroup, right: UserGroup): number {
  if (left.sortKey !== right.sortKey) {
    return left.sortKey < right.sortKey ? -1 : 1;
  }
  return left.id < right.id ? -1 : 1;
}

function isLive(cache: MockCache, group: UserGroup): boolean {
  return group.workspaceId === cache.activeWorkspaceId && group.archivedAt === null;
}

export function selectSystemUserGroups(cache: MockCache): UserGroup[] {
  return systemGroups(cache);
}

export function selectCustomUserGroups(cache: MockCache): UserGroup[] {
  return [...cache.userGroups.values()].filter((group) => isLive(cache, group)).sort(bySortKey);
}

export function selectUserGroups(cache: MockCache): UserGroup[] {
  return [...systemGroups(cache), ...selectCustomUserGroups(cache)];
}

export function selectUserGroup(cache: MockCache, id: UserGroupId): UserGroup | undefined {
  if (SYSTEM_GROUP_ROLES.has(id)) {
    return systemGroups(cache).find((group) => group.id === id);
  }
  const group = cache.userGroups.get(id);
  return group !== undefined && isLive(cache, group) ? group : undefined;
}

function usersWithRole(cache: MockCache, role: UserRole): User[] {
  return [...cache.users.values()]
    .filter((user) => user.role === role)
    .sort((left, right) => left.displayName.localeCompare(right.displayName, 'ru'));
}

export function selectGroupMembers(cache: MockCache, groupId: UserGroupId): User[] {
  const role = systemGroupRole(groupId);
  if (role !== null) {
    return usersWithRole(cache, role);
  }
  const memberships = [...cache.userGroupMembers.values()]
    .filter((member) => member.groupId === groupId)
    .sort((left, right) => (left.addedAt !== right.addedAt ? left.addedAt - right.addedAt : left.userId < right.userId ? -1 : 1));
  return memberships.flatMap((member) => {
    const user = cache.users.get(member.userId);
    return user === undefined ? [] : [user];
  });
}

export function selectGroupMemberCount(cache: MockCache, groupId: UserGroupId): number {
  return selectGroupMembers(cache, groupId).length;
}

export function selectGroupsOfUser(cache: MockCache, userId: UserId): UserGroup[] {
  const user = cache.users.get(userId);
  const roleGroupId = user === undefined ? null : ROLE_GROUP_IDS[user.role];
  return selectUserGroups(cache).filter(
    (group) => group.id === roleGroupId || cache.userGroupMembers.has(`${group.id}:${userId}`),
  );
}

export function selectActiveAdminCount(cache: MockCache): number {
  return usersWithRole(cache, 'admin').filter((user) => user.active).length;
}
